import { useCallback, useEffect, useState } from "react";
import { EventType, LEAType } from "../../../../api/events";
import { LeaReportsData } from "../../../../api/lea";
import { useAppSelector } from "../../../../app/store";
import { selectEventByTypeAndEntityId } from "../../../timeline/logic/slice";
import { selectLeaReportByType } from "../../logic/leaSlice";
import {
  EventDateInputCell,
  ReportFormRow,
  TextAreaInputCell,
} from "../common/ReportTableForm";

export interface LeaDetailsProps {
  onChange: (data: LeaReportsData | undefined) => void;
  discardChangesTrigger: number;
}

export const useLeaDetailsData = (
  onChange: (data: LeaReportsData | undefined) => void,
  discardChangesTrigger: number
) => {
  const statePoliceData = useAppSelector((state) =>
    selectLeaReportByType(state, LEAType.STATE_POLICE)
  );
  const [statePoliceFirNumber, setStatePoliceFirNumber] = useState("");
  const [statePoliceAddress, setStatePoliceAddress] = useState("");
  const [statePoliceStatus, setStatePoliceStatus] = useState("");
  const statePoliceComplaintEvent = useAppSelector(
    selectEventByTypeAndEntityId(
      EventType.COMPLAINT_LODGED_WITH_LEA,
      LEAType.STATE_POLICE
    )
  );
  const statePoliceFirEvent = useAppSelector(
    selectEventByTypeAndEntityId(
      EventType.FIR_FILED_WITH_LEA,
      LEAType.STATE_POLICE
    )
  );

  const cbiData = useAppSelector((state) =>
    selectLeaReportByType(state, LEAType.CBI)
  );
  const [cbiFirNumber, setCbiFirNumber] = useState("");
  const [cbiAddress, setCbiAddress] = useState("");
  const [cbiStatus, setCbiStatus] = useState("");
  const cbiComplaintEvent = useAppSelector(
    selectEventByTypeAndEntityId(
      EventType.COMPLAINT_LODGED_WITH_LEA,
      LEAType.CBI
    )
  );
  const cbiFirEvent = useAppSelector(
    selectEventByTypeAndEntityId(EventType.FIR_FILED_WITH_LEA, LEAType.CBI)
  );

  const cyberPoliceData = useAppSelector((state) =>
    selectLeaReportByType(state, LEAType.CYBER_POLICE)
  );
  const [cyberPoliceFirNumber, setCyberPoliceFirNumber] = useState("");
  const [cyberPoliceAddress, setCyberPoliceAddress] = useState("");
  const [cyberPoliceStatus, setCyberPoliceStatus] = useState("");
  const cyberPoliceComplaintEvent = useAppSelector(
    selectEventByTypeAndEntityId(
      EventType.COMPLAINT_LODGED_WITH_LEA,
      LEAType.CYBER_POLICE
    )
  );
  const cyberPoliceFirEvent = useAppSelector(
    selectEventByTypeAndEntityId(
      EventType.FIR_FILED_WITH_LEA,
      LEAType.CYBER_POLICE
    )
  );

  const [eventModal, setEventModal] = useState<{
    isOpen: boolean;
    eventType?: EventType;
    entityId?: LEAType;
  }>({ isOpen: false });

  const seedFromRedux = useCallback(() => {
    setStatePoliceFirNumber(statePoliceData?.fir_number || "");
    setStatePoliceAddress(statePoliceData?.lea_main_address || "");
    setStatePoliceStatus(statePoliceData?.lea_status_detailed || "");

    setCbiFirNumber(cbiData?.fir_number || "");
    setCbiAddress(cbiData?.lea_main_address || "");
    setCbiStatus(cbiData?.lea_status_detailed || "");

    setCyberPoliceFirNumber(cyberPoliceData?.fir_number || "");
    setCyberPoliceAddress(cyberPoliceData?.lea_main_address || "");
    setCyberPoliceStatus(cyberPoliceData?.lea_status_detailed || "");
  }, [statePoliceData, cbiData, cyberPoliceData]);

  useEffect(() => {
    seedFromRedux();
  }, [seedFromRedux, discardChangesTrigger]);

  useEffect(() => {
    const baseStatePoliceFirNumber = statePoliceData?.fir_number || "";
    const baseStatePoliceAddress = statePoliceData?.lea_main_address || "";
    const baseStatePoliceStatus = statePoliceData?.lea_status_detailed || "";

    const baseCbiFirNumber = cbiData?.fir_number || "";
    const baseCbiAddress = cbiData?.lea_main_address || "";
    const baseCbiStatus = cbiData?.lea_status_detailed || "";

    const baseCyberPoliceFirNumber = cyberPoliceData?.fir_number || "";
    const baseCyberPoliceAddress = cyberPoliceData?.lea_main_address || "";
    const baseCyberPoliceStatus = cyberPoliceData?.lea_status_detailed || "";

    const trimmedStatePoliceFirNumber = statePoliceFirNumber.trim();
    const trimmedStatePoliceAddress = statePoliceAddress.trim();
    const trimmedStatePoliceStatus = statePoliceStatus.trim();
    const trimmedCbiFirNumber = cbiFirNumber.trim();
    const trimmedCbiAddress = cbiAddress.trim();
    const trimmedCbiStatus = cbiStatus.trim();
    const trimmedCyberPoliceFirNumber = cyberPoliceFirNumber.trim();
    const trimmedCyberPoliceAddress = cyberPoliceAddress.trim();
    const trimmedCyberPoliceStatus = cyberPoliceStatus.trim();

    const isDirty =
      trimmedStatePoliceFirNumber !== baseStatePoliceFirNumber ||
      trimmedStatePoliceAddress !== baseStatePoliceAddress ||
      trimmedStatePoliceStatus !== baseStatePoliceStatus ||
      trimmedCbiFirNumber !== baseCbiFirNumber ||
      trimmedCbiAddress !== baseCbiAddress ||
      trimmedCbiStatus !== baseCbiStatus ||
      trimmedCyberPoliceFirNumber !== baseCyberPoliceFirNumber ||
      trimmedCyberPoliceAddress !== baseCyberPoliceAddress ||
      trimmedCyberPoliceStatus !== baseCyberPoliceStatus;

    if (isDirty) {
      const dirtyData: LeaReportsData = {};

      if (
        trimmedStatePoliceFirNumber ||
        trimmedStatePoliceAddress ||
        trimmedStatePoliceStatus
      ) {
        dirtyData[LEAType.STATE_POLICE] = {
          fir_number: trimmedStatePoliceFirNumber || "",
          lea_main_address: trimmedStatePoliceAddress || "",
          lea_status_detailed: trimmedStatePoliceStatus || null,
        };
      }

      if (trimmedCbiFirNumber || trimmedCbiAddress || trimmedCbiStatus) {
        dirtyData[LEAType.CBI] = {
          fir_number: trimmedCbiFirNumber || "",
          lea_main_address: trimmedCbiAddress || "",
          lea_status_detailed: trimmedCbiStatus || null,
        };
      }

      if (
        trimmedCyberPoliceFirNumber ||
        trimmedCyberPoliceAddress ||
        trimmedCyberPoliceStatus
      ) {
        dirtyData[LEAType.CYBER_POLICE] = {
          fir_number: trimmedCyberPoliceFirNumber || "",
          lea_main_address: trimmedCyberPoliceAddress || "",
          lea_status_detailed: trimmedCyberPoliceStatus || null,
        };
      }

      const hasValidData = Object.values(dirtyData).some((data) => {
        if (!data) return false;
        return (
          data.fir_number.trim() ||
          data.lea_main_address.trim() ||
          (data.lea_status_detailed && data.lea_status_detailed.trim())
        );
      });

      onChange(hasValidData ? dirtyData : undefined);
    } else {
      onChange(undefined);
    }
  }, [
    statePoliceFirNumber,
    statePoliceAddress,
    statePoliceStatus,
    cbiFirNumber,
    cbiAddress,
    cbiStatus,
    cyberPoliceFirNumber,
    cyberPoliceAddress,
    cyberPoliceStatus,
    statePoliceData,
    cbiData,
    cyberPoliceData,
    onChange,
  ]);

  const openAddEventModal = (eventType: EventType, entityId: LEAType) => {
    setEventModal({
      isOpen: true,
      eventType: eventType,
      entityId: entityId,
    });
  };

  const createLeaFormRows = (
    leaType: LEAType,
    complaintEvent: any,
    firEvent: any,
    firNumber: string,
    address: string,
    status: string,
    setFirNumber: (value: string) => void,
    setAddress: (value: string) => void,
    setStatus: (value: string) => void
  ): ReportFormRow[] => {
    return [
     //TODO: For future scope - Date of Mandate
      // {
      //   label: "Date of Mandate",
      //   inputCell: (
      //     <EventDateInputCell
      //       value={undefined}
      //       onClick={() => {}}
      //       disabled={true}
      //     />
      //   ),
      // },
      {
        label: "Date of Complaint Lodged",
        inputCell: (
          <EventDateInputCell
            value={complaintEvent?.date || undefined}
            onClick={() => {
              openAddEventModal(EventType.COMPLAINT_LODGED_WITH_LEA, leaType);
            }}
            disabled={!!complaintEvent?.date}
          />
        ),
      },
      {
        label: "Date of FIR",
        inputCell: (
          <EventDateInputCell
            value={firEvent?.date || undefined}
            onClick={() => {
              openAddEventModal(EventType.FIR_FILED_WITH_LEA, leaType);
            }}
            disabled={!!firEvent?.date}
          />
        ),
      },
      {
        label: "FIR Number",
        inputCell: (
          <TextAreaInputCell
            value={firNumber}
            onChange={(value) => {
              setFirNumber(value);
            }}
            onBlur={(value) => {
              const trimmed = value.trim();
              if (trimmed !== value) {
                setFirNumber(trimmed);
              }
            }}
            placeholder="Enter FIR number"
            disableResize={true}
            rows={1}
            textAreaClassName="!min-h-0"
          />
        ),
      },
      {
        label: "Address",
        inputCell: (
          <TextAreaInputCell
            value={address}
            onChange={(value) => {
              setAddress(value);
            }}
            onBlur={(value) => {
              const trimmed = value.trim();
              if (trimmed !== value) {
                setAddress(trimmed);
              }
            }}
            placeholder="Enter address of the LEA where fraud was reported"
            disableResize={true}
          />
        ),
      },
      {
        label: "Status",
        inputCell: (
          <TextAreaInputCell
            value={status}
            onChange={(value) => {
              setStatus(value);
            }}
            onBlur={(value) => {
              const trimmed = value.trim();
              if (trimmed !== value) {
                setStatus(trimmed);
              }
            }}
            placeholder="Enter Status"
          />
        ),
      },
    ];
  };

  const statePoliceFormRows = createLeaFormRows(
    LEAType.STATE_POLICE,
    statePoliceComplaintEvent,
    statePoliceFirEvent,
    statePoliceFirNumber,
    statePoliceAddress,
    statePoliceStatus,
    setStatePoliceFirNumber,
    setStatePoliceAddress,
    setStatePoliceStatus
  );

  const cbiFormRows = createLeaFormRows(
    LEAType.CBI,
    cbiComplaintEvent,
    cbiFirEvent,
    cbiFirNumber,
    cbiAddress,
    cbiStatus,
    setCbiFirNumber,
    setCbiAddress,
    setCbiStatus
  );

  const cyberPoliceFormRows = createLeaFormRows(
    LEAType.CYBER_POLICE,
    cyberPoliceComplaintEvent,
    cyberPoliceFirEvent,
    cyberPoliceFirNumber,
    cyberPoliceAddress,
    cyberPoliceStatus,
    setCyberPoliceFirNumber,
    setCyberPoliceAddress,
    setCyberPoliceStatus
  );

  return {
    statePoliceFormRows,
    cbiFormRows,
    cyberPoliceFormRows,
    eventModal,
    setEventModal,
  };
};

